import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Button, Input, Space, Modal, Form, Tag, Popconfirm,
  message, Card, Typography, Row, Col, Switch, InputNumber,
  Alert, Descriptions, AutoComplete
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  ApiOutlined, ThunderboltOutlined, SettingOutlined, ReloadOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import { useActionColumn } from '../utils/tableLayout';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;

function AIConfig() {
  const actionColumn = useActionColumn(220);
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [detectingModels, setDetectingModels] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelFeedback, setModelFeedback] = useState(null);
  const modelRequestId = useRef(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: ''
  });

  const [form] = Form.useForm();

  useEffect(() => () => { modelRequestId.current += 1; }, []);

  const resetModelDetection = () => {
    modelRequestId.current += 1;
    setDetectingModels(false);
    setModelOptions([]);
    setModelFeedback(null);
  };

  const closeConfigModal = () => {
    resetModelDetection();
    setModalVisible(false);
  };

  const handleDetectModels = async () => {
    const requestId = ++modelRequestId.current;
    setDetectingModels(true);
    setModelFeedback(null);
    try {
      const values = await form.validateFields(['apiUrl', 'apiKey']);
      if (requestId !== modelRequestId.current) return;
      const payload = { apiUrl: values.apiUrl.trim() };
      if (values.apiKey?.trim()) payload.apiKey = values.apiKey.trim();
      else if (editingRecord) payload.configId = editingRecord._id;
      const response = await api.post('/ai-config/detect-models', payload);
      if (requestId !== modelRequestId.current) return;
      const options = response.data.models.map(model => ({ value: model.id, label: model.id }));
      setModelOptions(options);
      setModelFeedback(options.length ? {
        type: 'success', message: `检测到 ${options.length} 个模型，可在下方搜索或选择。模型是否支持聊天解析，请保存后使用“测试”确认。`
      } : {
        type: 'warning', message: '接口返回的模型列表为空，请检查密钥权限，也可手动输入模型名称。'
      });
    } catch (error) {
      if (requestId !== modelRequestId.current) return;
      setModelOptions([]);
      if (!error.errorFields) {
        setModelFeedback({ type: 'error', message: error.response?.data?.message || '检测模型失败，请检查网络后重试，也可手动输入模型名称。' });
      }
    } finally {
      if (requestId === modelRequestId.current) setDetectingModels(false);
    }
  };

  useEffect(() => {
    loadConfigs();
    loadActiveConfig();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      const response = await api.get('/ai-config', { params });
      setConfigs(response.data.configs);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('加载AI配置失败:', error);
      message.error('加载AI配置失败');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveConfig = async () => {
    try {
      const response = await api.get('/ai-config/active');
      setActiveConfig(response.data.config);
    } catch {
      // 忽略
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    resetModelDetection();
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      temperature: 0.1,
      maxTokens: 1024,
      timeoutSeconds: 30,
      isActive: false
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    resetModelDetection();
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      apiUrl: record.apiUrl,
      apiKey: '',  // 不回填密钥
      model: record.model,
      temperature: record.temperature,
      maxTokens: record.maxTokens,
      timeoutSeconds: record.timeoutSeconds || 30,
      description: record.description,
      isActive: record.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ai-config/${id}`);
      message.success('删除成功');
      loadConfigs();
      loadActiveConfig();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = { ...values };
      // 编辑时如果 apiKey 为空则不提交（保留原值）
      if (editingRecord && !data.apiKey) {
        delete data.apiKey;
      }

      if (editingRecord) {
        await api.put(`/ai-config/${editingRecord._id}`, data);
        message.success('更新成功');
      } else {
        await api.post('/ai-config', data);
        message.success('创建成功');
      }
      closeConfigModal();
      loadConfigs();
      loadActiveConfig();
    } catch (error) {
      console.error('保存失败:', error);
      const errorMessage = error.response?.data?.message || '保存失败';
      message.error(errorMessage);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await api.patch(`/ai-config/${record._id}/toggle`);
      message.success(`配置已${record.isActive ? '停用' : '激活'}`);
      loadConfigs();
      loadActiveConfig();
    } catch (error) {
      console.error('切换状态失败:', error);
      message.error('切换状态失败');
    }
  };

  const handleTest = async (record) => {
    try {
      setTestingId(record._id);
      const response = await api.post(`/ai-config/${record._id}/test`);
      if (response.data.success) {
        message.success(response.data.message);
      } else {
        message.error(response.data.message);
      }
      loadConfigs();
    } catch (error) {
      console.error('测试连接失败:', error);
      message.error('测试连接失败');
    } finally {
      setTestingId(null);
    }
  };

  const handleViewDetail = (record) => {
    setSelectedConfig(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'API地址',
      dataIndex: 'apiUrl',
      key: 'apiUrl',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text copyable style={{ fontSize: '12px' }}>{text}</Typography.Text>
      )
    },
    {
      title: '模型',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (text) => <Tag>{text}</Tag>
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.isActive ? 'green' : 'default'}>
          {record.isActive ? '使用中' : '未激活'}
        </Tag>
      )
    },
    {
      title: '使用统计',
      key: 'usage',
      width: 100,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.usageCount || 0}次</div>
          {record.lastUsedAt && (
            <div style={{ color: '#666' }}>
              {moment(record.lastUsedAt).format('MM-DD HH:mm')}
            </div>
          )}
        </div>
      )
    },
    {
      title: '上次测试',
      key: 'lastTest',
      width: 120,
      render: (_, record) => {
        if (!record.lastTestResult || !record.lastTestResult.testedAt) return <Text type="secondary">未测试</Text>;
        return (
          <div style={{ fontSize: '12px' }}>
            <Tag color={record.lastTestResult.success ? 'green' : 'red'} style={{ fontSize: '11px' }}>
              {record.lastTestResult.success ? '通过' : '失败'}
            </Tag>
            {record.lastTestResult.responseTime && (
              <span style={{ color: '#666' }}> {record.lastTestResult.responseTime}ms</span>
            )}
          </div>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      ...actionColumn,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<ApiOutlined />}
            loading={testingId === record._id}
            onClick={() => handleTest(record)}
          >
            测试
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ThunderboltOutlined />}
            onClick={() => handleToggleActive(record)}
          >
            {record.isActive ? '停用' : '激活'}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个配置吗？"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>AI模型配置</Title>
        <div className="description">配置 AI 模型，用于提取报名人员信息</div>
      </div>

      {activeConfig && (
        <Alert
          message={`当前使用: ${activeConfig.name} (${activeConfig.model})`}
          description={`API: ${activeConfig.apiUrl}`}
          type="info"
          showIcon
          icon={<SettingOutlined />}
          style={{ marginBottom: 16 }}
          closable={false}
        />
      )}

      {!activeConfig && (
        <Alert
          message="未配置AI模型"
          description="未配置 AI 时使用规则提取；提取失败会保留原消息，并按推送反馈配置通知"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <div className="table-toolbar">
          <div className="left">
            <Input.Search
              placeholder="搜索配置名称或模型"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
          </div>
          <div className="right">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加配置
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={configs}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1170 }}
          tableLayout="fixed"
          size="small"
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑AI配置' : '添加AI配置'}
        open={modalVisible}
        onCancel={closeConfigModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changed) => {
            if ('apiUrl' in changed || 'apiKey' in changed) resetModelDetection();
          }}
        >
          <Form.Item
            name="name"
            label="配置名称"
            rules={[{ required: true, message: '请输入配置名称' }]}
          >
            <Input placeholder="如：MiniMax生产环境" />
          </Form.Item>

          <Form.Item
            name="apiUrl"
            label="API地址"
            rules={[{ required: true, message: '请输入API地址' }]}
          >
            <Input placeholder="如：https://api.minimax.chat/v1" />
          </Form.Item>

          <Form.Item
            name="apiKey"
            label="API Key"
            rules={editingRecord ? [] : [{ required: true, message: '请输入API Key' }]}
          >
            <Input.Password placeholder={editingRecord ? '留空则保留原密钥' : '请输入API Key'} />
          </Form.Item>

          <Form.Item
            style={{ marginBottom: 16 }}
          >
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={detectingModels} onClick={handleDetectModels}>
                检测可用模型
              </Button>
              <Text type="secondary">先填写 API 地址和密钥，编辑时可使用已保存的密钥。</Text>
            </Space>
            {modelFeedback && (
              <Alert role="status" type={modelFeedback.type} showIcon message={modelFeedback.message} style={{ marginTop: 12 }} />
            )}
          </Form.Item>

          <Form.Item
            name="model"
            label="模型名称"
            rules={[{ required: true, message: '请输入模型名称' }]}
            extra="支持从检测结果中搜索选择，也可直接输入服务商提供的模型名称。"
          >
            <AutoComplete
              options={modelOptions}
              allowClear
              placeholder="检测后选择，或手动输入模型名称"
              filterOption={(value, option) => option.value.toLowerCase().includes(value.toLowerCase())}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="temperature"
                label="Temperature"
              >
                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxTokens"
                label="Max Tokens"
              >
                <InputNumber min={1} max={8192} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="timeoutSeconds"
                label="匹配等待时间（秒）"
                extra="5～120 秒，控制每次 AI 提取和选岗。试匹配、自动处理和手动推送共用；页面会自动预留完整流程的等待时间。"
                rules={[{ required: true, message: '请输入匹配等待时间' }, { type: 'integer', min: 5, max: 120, message: '请输入 5～120 秒的整数' }]}
              >
                <InputNumber min={5} max={120} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea placeholder="备注信息" rows={2} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="激活（同时只允许一个激活）"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '创建'}
              </Button>
              <Button onClick={closeConfigModal}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="配置详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {selectedConfig && (
          <Descriptions className="configuration-details" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
            <Descriptions.Item label="配置名称">{selectedConfig.name}</Descriptions.Item>
            <Descriptions.Item label="模型">{selectedConfig.model}</Descriptions.Item>
            <Descriptions.Item label="API地址" span="filled">
              <Typography.Text copyable style={{ fontSize: '12px' }}>{selectedConfig.apiUrl}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="API Key">
              <Typography.Text code>{selectedConfig.apiKey}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedConfig.isActive ? 'green' : 'default'}>
                {selectedConfig.isActive ? '使用中' : '未激活'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Temperature">{selectedConfig.temperature}</Descriptions.Item>
            <Descriptions.Item label="Max Tokens">{selectedConfig.maxTokens}</Descriptions.Item>
            <Descriptions.Item label="匹配等待时间">{selectedConfig.timeoutSeconds || 30} 秒 / 次 AI 调用</Descriptions.Item>
            <Descriptions.Item label="使用次数">{selectedConfig.usageCount || 0}</Descriptions.Item>
            <Descriptions.Item label="最后使用">
              {selectedConfig.lastUsedAt ? moment(selectedConfig.lastUsedAt).format('YYYY-MM-DD HH:mm:ss') : '从未使用'}
            </Descriptions.Item>
            {selectedConfig.description && (
              <Descriptions.Item label="描述" span="filled">{selectedConfig.description}</Descriptions.Item>
            )}
            {selectedConfig.lastTestResult && selectedConfig.lastTestResult.testedAt && (
              <Descriptions.Item label="上次测试" span="filled">
                <Tag color={selectedConfig.lastTestResult.success ? 'green' : 'red'}>
                  {selectedConfig.lastTestResult.success ? '成功' : '失败'}
                </Tag>
                {selectedConfig.lastTestResult.responseTime}ms
                <span style={{ marginLeft: 8, color: '#666' }}>
                  {moment(selectedConfig.lastTestResult.testedAt).format('YYYY-MM-DD HH:mm')}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">
              {moment(selectedConfig.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {moment(selectedConfig.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}

export default AIConfig;
